import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { visuallyHidden } from "@mui/utils";
import dayjs, { Dayjs } from "dayjs";
import {
  ClioService,
  LloFunnelReportItem,
  LloFunnelReportRequest,
  OpenAPI,
  UsersModel,
} from "../../../api";
import FilterableSelect from "../../../components/basic/FilterableSelect";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);
const TEAM_MEMBER_ALL = "";
const COLUMN_COUNT = 12;

const CASE_LEVEL_COLUMNS: SortableColumn[] = [
  "caseName",
  "raa",
  "ap",
  "lloExpiration",
  "negotiation",
  "upcomingDeadlines",
];

type SortableColumn =
  | "caseName"
  | "raa"
  | "ap"
  | "upcomingDeadlines"
  | "lloExpiration"
  | "negotiation"
  | "funnel"
  | "dueDate"
  | "taskName"
  | "taskAssignee"
  | "description";

type SortDirection = "asc" | "desc";

type RowWithSpans = LloFunnelReportItem & {
  rowNumber?: number;
  groupRowSpan?: number;
  skipCaseColumns?: boolean;
};

const UPCOMING_DEADLINES_WIDTH = 155;

const HEAD_CELLS: {
  id: SortableColumn;
  label: string;
  width?: number;
  minWidth?: number;
  flex?: boolean;
}[] = [
  { id: "caseName", label: "Case Name", width: 150 },
  { id: "raa", label: "RAA", width: 68 },
  { id: "ap", label: "AP", width: 68 },
  { id: "lloExpiration", label: "LLO Expiration", width: 98 },
  { id: "negotiation", label: "Negotiation", width: 78 },
  { id: "funnel", label: "Funnel", width: 230 },
  { id: "dueDate", label: "Due Date", width: 92 },
  { id: "taskName", label: "Task", width: 190 },
  { id: "taskAssignee", label: "Task Assignee", width: 96 },
  { id: "description", label: "Description", minWidth: 360, flex: true },
  { id: "upcomingDeadlines", label: "Upcoming Deadlines", width: UPCOMING_DEADLINES_WIDTH },
];

const CASE_GROUP_HEAD_CELL_IDS: SortableColumn[] = [
  "caseName",
  "raa",
  "ap",
  "lloExpiration",
  "negotiation",
];

const CASE_GROUP_END_HEAD_CELL_IDS: SortableColumn[] = ["upcomingDeadlines"];

const TASK_HEAD_CELL_IDS: SortableColumn[] = [
  "funnel",
  "dueDate",
  "taskName",
  "taskAssignee",
  "description",
];

const TABLE_HEADER_CELL_SX = {
  fontWeight: 600,
  fontSize: "0.8125rem",
  backgroundColor: "#6b7280",
  color: "#fff",
  borderBottom: "none",
  whiteSpace: "nowrap" as const,
  py: 1.25,
  "& .MuiTableSortLabel-root": { color: "rgba(255,255,255,0.85)" },
  "& .MuiTableSortLabel-root:hover": { color: "#fff" },
  "& .MuiTableSortLabel-root.Mui-active": { color: "#fff" },
  "& .MuiTableSortLabel-icon": { color: "#fff !important" },
};

const TABLE_BODY_CELL_SX = {
  fontSize: "0.8125rem",
  py: 1.25,
  borderColor: "divider",
  verticalAlign: "top" as const,
};

function getHeadCellSx(headCell: (typeof HEAD_CELLS)[number]) {
  if (headCell.flex) {
    return { ...TABLE_HEADER_CELL_SX, minWidth: headCell.minWidth, width: "auto" };
  }

  return {
    ...TABLE_HEADER_CELL_SX,
    width: headCell.width,
    minWidth: headCell.width,
    maxWidth: headCell.width,
  };
}

function getBodyCellSx(
  headCell: (typeof HEAD_CELLS)[number],
  extra: Record<string, unknown> = {}
) {
  if (headCell.flex) {
    return { ...TABLE_BODY_CELL_SX, minWidth: headCell.minWidth, ...extra };
  }

  return {
    ...TABLE_BODY_CELL_SX,
    width: headCell.width,
    minWidth: headCell.width,
    maxWidth: headCell.width,
    ...extra,
  };
}

const HEAD_CELL_BY_ID = Object.fromEntries(HEAD_CELLS.map((cell) => [cell.id, cell])) as Record<
  SortableColumn,
  (typeof HEAD_CELLS)[number]
>;

const matterGroupCellStyle = {
  borderBottom: "2px solid",
  borderColor: "divider",
  verticalAlign: "top" as const,
};

const OVERDUE_ROW_BG = "#fdecea";
const OVERDUE_ROW_HOVER_BG = "#f9d5d2";

function getRowCellSx(overdue: boolean) {
  return {
    ...TABLE_BODY_CELL_SX,
    ...(overdue ? { bgcolor: OVERDUE_ROW_BG } : {}),
  };
}

function normalizeText(value?: string | null) {
  if (!value) return "";
  return value.replace(/\r\n|\n\r|\r/g, "\n");
}

function containsHtml(value: string) {
  return /<[a-z][\s\S]*>/i.test(value);
}

function stripHtml(value: string) {
  return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function sanitizeTaskDescriptionHtml(html: string) {
  const doc = new DOMParser().parseFromString(html, "text/html");

  doc.querySelectorAll("script, style, iframe, object, embed, form").forEach((el) => el.remove());

  doc.body.querySelectorAll("*").forEach((el) => {
    [...el.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name.startsWith("on")) {
        el.removeAttribute(attr.name);
      }
    });

    if (el.tagName === "A") {
      el.setAttribute("target", "_blank");
      el.setAttribute("rel", "noopener noreferrer");
    }
  });

  return doc.body.innerHTML;
}

const descriptionContentSx = {
  whiteSpace: "pre-wrap" as const,
  wordBreak: "break-word" as const,
  "& a": {
    color: "primary.main",
    textDecoration: "underline",
  },
  "& p": { margin: 0 },
  "& ul, & ol": { margin: 0, paddingLeft: 2 },
};

function DescriptionCell({ text }: { text?: string | null }) {
  const content = text ?? "";
  if (!content.trim()) {
    return <>—</>;
  }

  if (!containsHtml(content)) {
    return <Box sx={descriptionContentSx}>{content}</Box>;
  }

  return (
    <Box
      sx={descriptionContentSx}
      dangerouslySetInnerHTML={{ __html: sanitizeTaskDescriptionHtml(content) }}
    />
  );
}

function getMainFunnelTabOrder(funnel?: string | null): number {
  const value = funnel ?? "";
  const prefix = "Main Funnel — ";
  if (!value.startsWith(prefix)) {
    return 99;
  }

  const tab = value.slice(prefix.length);
  const order: Record<string, number> = {
    Complaint: 0,
    "Discovery Requests": 1,
    "Plaintiff's Discovery Responses": 2,
    "Judicial Assignment Responsive": 3,
    "Case Management Conference": 4,
  };

  return order[tab] ?? 99;
}

function getFunnelCategoryOrder(funnel?: string | null): number {
  const value = funnel ?? "";
  if (value.startsWith("Main Funnel")) return 0;
  if (value.startsWith("Secondary Funnel")) return 1;
  if (value.startsWith("Single Task")) return 2;
  if (value === "Manual") return 3;
  return 4;
}

function getSortValue(row: LloFunnelReportItem, column: SortableColumn): string | number {
  switch (column) {
    case "caseName":
      return row.caseName?.toLowerCase() ?? "";
    case "raa":
      return row.raa?.toLowerCase() ?? "";
    case "ap":
      return row.ap?.toLowerCase() ?? "";
    case "upcomingDeadlines":
      return row.upcomingDeadlines?.toLowerCase() ?? "";
    case "lloExpiration":
      return row.lloExpiration?.toLowerCase() ?? "";
    case "negotiation":
      return row.negotiation?.toLowerCase() ?? "";
    case "funnel":
      return `${getFunnelCategoryOrder(row.funnel)}|${getMainFunnelTabOrder(row.funnel)}|${row.funnel?.toLowerCase() ?? ""}`;
    case "taskName":
      return row.taskName?.toLowerCase() ?? "";
    case "taskAssignee":
      return row.taskAssignee?.toLowerCase() ?? "";
    case "description":
      return stripHtml(row.description ?? "").toLowerCase();
    case "dueDate":
      return row.dueDate ? dayjs(row.dueDate).valueOf() : Number.MAX_SAFE_INTEGER;
    default:
      return "";
  }
}

function compareSortValues(
  aValue: string | number,
  bValue: string | number,
  order: SortDirection
) {
  if (aValue < bValue) return order === "asc" ? -1 : 1;
  if (aValue > bValue) return order === "asc" ? 1 : -1;
  return 0;
}

function getDefaultTaskOrder(a: LloFunnelReportItem, b: LloFunnelReportItem) {
  const categoryOrder = getFunnelCategoryOrder(a.funnel) - getFunnelCategoryOrder(b.funnel);
  if (categoryOrder !== 0) return categoryOrder;

  const tabOrder = getMainFunnelTabOrder(a.funnel) - getMainFunnelTabOrder(b.funnel);
  if (tabOrder !== 0) return tabOrder;

  const funnelCompare = (a.funnel ?? "").localeCompare(b.funnel ?? "");
  if (funnelCompare !== 0) return funnelCompare;

  const aDue = a.dueDate ? dayjs(a.dueDate).valueOf() : Number.MAX_SAFE_INTEGER;
  const bDue = b.dueDate ? dayjs(b.dueDate).valueOf() : Number.MAX_SAFE_INTEGER;
  return aDue - bDue;
}

function compareTaskRows(
  a: LloFunnelReportItem,
  b: LloFunnelReportItem,
  orderBy: SortableColumn,
  order: SortDirection
) {
  const primary = compareSortValues(getSortValue(a, orderBy), getSortValue(b, orderBy), order);
  if (primary !== 0) return primary;
  return getDefaultTaskOrder(a, b);
}

function getGroupSortValue(group: LloFunnelReportItem[], orderBy: SortableColumn): string | number {
  const first = group[0];
  if (!first) return "";

  if (CASE_LEVEL_COLUMNS.includes(orderBy)) {
    return getSortValue(first, orderBy);
  }

  if (orderBy === "dueDate") {
    return Math.min(
      ...group.map((row) => (row.dueDate ? dayjs(row.dueDate).valueOf() : Number.MAX_SAFE_INTEGER))
    );
  }

  return group.reduce<string | number>((best, row) => {
    const value = getSortValue(row, orderBy);
    if (best === "") return value;
    return compareSortValues(value, best, "asc") < 0 ? value : best;
  }, "");
}

function groupRowsByCase(rows: LloFunnelReportItem[]): LloFunnelReportItem[][] {
  const groups: LloFunnelReportItem[][] = [];
  let currentGroup: LloFunnelReportItem[] = [];
  let currentCaseName = "";

  rows.forEach((row) => {
    if (row.caseName !== currentCaseName) {
      if (currentGroup.length > 0) {
        groups.push(currentGroup);
      }
      currentGroup = [row];
      currentCaseName = row.caseName ?? "";
    } else {
      currentGroup.push(row);
    }
  });

  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }

  return groups;
}

function sortReportRows(
  rows: LloFunnelReportItem[],
  orderBy: SortableColumn,
  order: SortDirection
): LloFunnelReportItem[] {
  const groups = groupRowsByCase(rows);

  groups.sort((groupA, groupB) => {
    const primary = compareSortValues(
      getGroupSortValue(groupA, orderBy),
      getGroupSortValue(groupB, orderBy),
      order
    );
    if (primary !== 0) return primary;
    return (groupA[0]?.caseName ?? "").localeCompare(groupB[0]?.caseName ?? "");
  });

  return groups.flatMap((group) =>
    [...group].sort((a, b) =>
      CASE_LEVEL_COLUMNS.includes(orderBy)
        ? getDefaultTaskOrder(a, b)
        : compareTaskRows(a, b, orderBy, order)
    )
  );
}

function computeCaseGroupSpans(rows: LloFunnelReportItem[]): RowWithSpans[] {
  const out: RowWithSpans[] = [];
  let i = 0;
  let caseNumber = 0;

  while (i < rows.length) {
    const caseName = rows[i]?.caseName ?? "";
    let j = i + 1;
    while (j < rows.length && (rows[j]?.caseName ?? "") === caseName) j++;

    const span = j - i;
    caseNumber += 1;

    for (let k = i; k < j; k++) {
      out.push({
        ...rows[k],
        rowNumber: k === i ? caseNumber : undefined,
        groupRowSpan: k === i ? span : undefined,
        skipCaseColumns: k !== i,
      });
    }

    i = j;
  }

  return out;
}

function buildTeamMemberOptions(users: UsersModel[]) {
  const nameCount = new Map<string, number>();
  users.forEach((user) => {
    const name = user.name?.trim() ?? "";
    if (name) {
      nameCount.set(name, (nameCount.get(name) ?? 0) + 1);
    }
  });

  const seenIds = new Set<number>();
  return users
    .filter((user) => user.id != null && !seenIds.has(user.id) && seenIds.add(user.id!))
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
    .map((user) => {
      const name = user.name?.trim() || `User ${user.id}`;
      const label =
        (nameCount.get(user.name?.trim() ?? "") ?? 0) > 1 && user.email
          ? `${name} (${user.email})`
          : name;

      return {
        value: user.id!,
        label,
      };
    });
}

function UpcomingDeadlinesCell({ text }: { text?: string | null }) {
  const [expanded, setExpanded] = useState(false);
  const [overflow, setOverflow] = useState(false);
  const raw = normalizeText(text);
  const isEmpty = !raw.trim() || raw.trim().toLowerCase() === "n/a";

  if (isEmpty) {
    return <>—</>;
  }

  return (
    <Box>
      <Box
        ref={(el: HTMLDivElement | null) => {
          if (!el || expanded) return;
          const isOverflowing = el.scrollHeight > el.clientHeight + 1;
          setOverflow((prev) => (prev === isOverflowing ? prev : isOverflowing));
        }}
        sx={
          expanded
            ? { whiteSpace: "pre-wrap", wordBreak: "break-word" }
            : {
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 3,
                overflow: "hidden",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }
        }
      >
        {raw}
      </Box>
      {overflow && (
        <Link
          component="button"
          type="button"
          variant="caption"
          onClick={() => setExpanded((prev) => !prev)}
          sx={{ mt: 0.5, display: "inline-block" }}
        >
          {expanded ? "Show less" : "Show more"}
        </Link>
      )}
    </Box>
  );
}

export default function LloFunnelReport() {
  const [reportData, setReportData] = useState<LloFunnelReportItem[]>([]);
  const [usersList, setUsersList] = useState<UsersModel[]>([]);
  const [teamMemberId, setTeamMemberId] = useState<number | "">("");
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<SortDirection>("asc");
  const [orderBy, setOrderBy] = useState<SortableColumn>("caseName");

  const teamMemberOptions = useMemo(() => buildTeamMemberOptions(usersList), [usersList]);

  const hasActiveFilters =
    teamMemberId !== "" || startDate != null || endDate != null || searchTerm.trim().length > 0;

  useEffect(() => {
    ClioService.getApiClioClioUsers().then((users) => {
      setUsersList(users);
    });
  }, []);

  const buildRequest = useCallback((): LloFunnelReportRequest => {
    const selectedUser =
      teamMemberId !== "" ? usersList.find((user) => user.id === teamMemberId) : null;

    return {
      teamMemberId: selectedUser?.id ?? null,
      teamMemberName: selectedUser?.name ?? null,
      startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
      endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
      searchTerm: searchTerm.trim() || null,
    };
  }, [endDate, searchTerm, startDate, teamMemberId, usersList]);

  const clearFilters = () => {
    setTeamMemberId("");
    setStartDate(null);
    setEndDate(null);
    setSearchTerm("");
  };

  const loadReport = useCallback(() => {
    setLoading(true);
    ClioService.postApiClioLloFunnelReport(buildRequest())
      .then((data) => {
        setReportData(data);
        setPage(0);
      })
      .catch(() =>
        MySwal.fire({
          icon: "error",
          title: <p>Error loading LLO funnel report.</p>,
        })
      )
      .finally(() => setLoading(false));
  }, [buildRequest]);

  const sortedData = useMemo(
    () => sortReportRows(reportData, orderBy, order),
    [reportData, order, orderBy]
  );

  const caseGroups = useMemo(() => groupRowsByCase(sortedData), [sortedData]);

  const paginatedRows = useMemo(() => {
    const pageGroups = caseGroups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    return computeCaseGroupSpans(pageGroups.flat());
  }, [caseGroups, page, rowsPerPage]);

  const uniqueCaseCount = caseGroups.length;

  const handleRequestSort = (column: SortableColumn) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
    setPage(0);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${OpenAPI.BASE}/api/Clio/lloFunnelReport/download`, {
        method: "POST",
        headers: {
          Authorization: (OpenAPI.HEADERS as Record<string, string>)?.Authorization,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(buildRequest()),
      });

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `LloFunnelReport-${dayjs().format("YYYY-MM-DD")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      MySwal.fire({
        icon: "error",
        title: <p>Error downloading LLO funnel report.</p>,
      });
    } finally {
      setDownloading(false);
    }
  };

  const isOverdue = (dueDate?: string | null) =>
    !!dueDate && dayjs(dueDate).isBefore(dayjs(), "day");

  return (
    <Box
      sx={{
        height: "calc(100vh - 120px)",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600} lineHeight={1.3}>
            LLO Case Funnel Overview
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Limited Litigation tasks with descriptions modified from the standard template.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={downloading ? <CircularProgress size={16} /> : <FileDownloadIcon />}
          onClick={handleDownload}
          disabled={downloading || loading || reportData.length === 0}
        >
          Download
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ px: 2, py: 1.5, flexShrink: 0, bgcolor: "grey.50" }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Filters
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          Choose optional filters, then click Load Report. Team member matches RAA, AP, or task
          assignee. Due dates apply to open tasks only.
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
          <FilterableSelect
            label="Team member"
            value={teamMemberId === "" ? TEAM_MEMBER_ALL : teamMemberId}
            options={teamMemberOptions}
            onChange={(value) => {
              if (value === undefined || value === TEAM_MEMBER_ALL || value === "") {
                setTeamMemberId("");
                return;
              }

              setTeamMemberId(Number(value));
            }}
            allOptionLabel="All team members"
            allOptionValue={TEAM_MEMBER_ALL}
            searchPlaceholder="Search by name..."
            minWidth={240}
          />
          <DatePicker
            label="Due date from"
            value={startDate}
            onChange={(value) => setStartDate(value)}
            slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
          />
          <DatePicker
            label="Due date to"
            value={endDate}
            onChange={(value) => setEndDate(value)}
            slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
          />
          <TextField
            label="Search"
            placeholder="Case, task, assignee, funnel..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: 280 }, flexGrow: { sm: 1 }, maxWidth: 340 }}
          />
          <Button variant="contained" size="small" onClick={loadReport} disabled={loading}>
            {loading ? <CircularProgress size={18} color="inherit" /> : "Load Report"}
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={clearFilters}
            disabled={loading || !hasActiveFilters}
          >
            Clear filters
          </Button>
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {reportData.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, display: "block" }}>
              {uniqueCaseCount} active case{uniqueCaseCount === 1 ? "" : "s"} · {reportData.length} task
              {reportData.length === 1 ? "" : "s"}
            </Typography>
            <Divider />
          </>
        )}

        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <Table size="small" stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...TABLE_HEADER_CELL_SX, width: 44, minWidth: 44 }}>#</TableCell>
                {HEAD_CELLS.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={getHeadCellSx(headCell)}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : "asc"}
                      onClick={() => handleRequestSort(headCell.id)}
                    >
                      {headCell.label}
                      {orderBy === headCell.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc" ? "sorted descending" : "sorted ascending"}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : paginatedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    {reportData.length === 0
                      ? "Click Load Report to view LLO funnel tasks."
                      : "No tasks match the selected filters."}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRows.map((row) => {
                  const overdue = isOverdue(row.dueDate);
                  const cellSx = getRowCellSx(overdue);
                  const groupCellSx = { ...matterGroupCellStyle, ...cellSx };

                  return (
                  <TableRow
                    key={row.taskId}
                    hover
                    sx={{
                      "&:hover .MuiTableCell-root": {
                        bgcolor: overdue ? OVERDUE_ROW_HOVER_BG : "action.hover",
                      },
                    }}
                  >
                    {!row.skipCaseColumns ? (
                      <>
                        <TableCell rowSpan={row.groupRowSpan} sx={groupCellSx}>
                          {row.rowNumber}
                        </TableCell>
                        {CASE_GROUP_HEAD_CELL_IDS.map((columnId) => (
                          <TableCell
                            key={columnId}
                            rowSpan={row.groupRowSpan}
                            sx={{
                              ...groupCellSx,
                              ...getBodyCellSx(HEAD_CELL_BY_ID[columnId], {
                                ...(columnId === "caseName" ? { fontWeight: 500 } : {}),
                                ...(columnId === "lloExpiration" ? { whiteSpace: "nowrap" } : {}),
                              }),
                            }}
                          >
                            {columnId === "caseName" && row.caseName}
                            {columnId === "raa" && row.raa}
                            {columnId === "ap" && row.ap}
                            {columnId === "lloExpiration" && (row.lloExpiration || "—")}
                            {columnId === "negotiation" && row.negotiation}
                          </TableCell>
                        ))}
                      </>
                    ) : null}
                    {TASK_HEAD_CELL_IDS.map((columnId) => {
                      if (columnId === "funnel") {
                        return (
                          <TableCell
                            key={columnId}
                            sx={{ ...cellSx, ...getBodyCellSx(HEAD_CELL_BY_ID.funnel) }}
                          >
                            {row.funnel}
                          </TableCell>
                        );
                      }

                      if (columnId === "dueDate") {
                        return (
                          <TableCell
                            key={columnId}
                            sx={{
                              ...cellSx,
                              ...getBodyCellSx(HEAD_CELL_BY_ID.dueDate, {
                                whiteSpace: "nowrap",
                                color: overdue ? "error.main" : "inherit",
                                fontWeight: overdue ? 600 : 400,
                              }),
                            }}
                          >
                            {row.dueDate ? dayjs(row.dueDate).format("MM/DD/YYYY") : "—"}
                          </TableCell>
                        );
                      }

                      if (columnId === "taskName") {
                        return (
                          <TableCell
                            key={columnId}
                            sx={{ ...cellSx, ...getBodyCellSx(HEAD_CELL_BY_ID.taskName) }}
                          >
                            {row.taskName}
                          </TableCell>
                        );
                      }

                      if (columnId === "taskAssignee") {
                        return (
                          <TableCell
                            key={columnId}
                            sx={{ ...cellSx, ...getBodyCellSx(HEAD_CELL_BY_ID.taskAssignee) }}
                          >
                            {row.taskAssignee}
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell
                          key={columnId}
                          sx={{ ...cellSx, ...getBodyCellSx(HEAD_CELL_BY_ID.description) }}
                        >
                          <DescriptionCell text={row.description} />
                        </TableCell>
                      );
                    })}
                    {!row.skipCaseColumns
                      ? CASE_GROUP_END_HEAD_CELL_IDS.map((columnId) => (
                          <TableCell
                            key={columnId}
                            rowSpan={row.groupRowSpan}
                            sx={{
                              ...groupCellSx,
                              ...getBodyCellSx(HEAD_CELL_BY_ID[columnId], { overflow: "hidden" }),
                            }}
                          >
                            <UpcomingDeadlinesCell text={row.upcomingDeadlines} />
                          </TableCell>
                        ))
                      : null}
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider />
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={caseGroups.length}
          rowsPerPage={rowsPerPage}
          page={page}
          labelRowsPerPage="Cases per page"
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{ flexShrink: 0, minHeight: 52 }}
        />
      </Paper>
    </Box>
  );
}
